import { withProjectCardStyle } from 'root/src/client/web/list/listItemComponents/style'
import { useDareCard } from 'root/src/client/web/list/listItemComponents/dare/hooks/useDareCard'
import { useBlackCardContext } from 'root/src/client/v2/common/components/BlackCard/hooks/useBlackCardContext'
import Link from 'next/link'
import LoadingButtonNew from 'root/src/client/web/base/LoadingButtonNew'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import classNames from 'classnames'
import { addHttpsProtocol } from 'root/src/shared/util/protocolUtil'
import {
	ProjectCardLabel,
	statusColorsMap,
	statusLabels,
} from 'root/src/client/web/list/listItemComponents/project/ProjectCardLabel'
import { ProjectTimeLeft } from 'root/src/client/web/list/listItemComponents/project/ProjectCardHeader'
import {
	dareExpiredMessage,
	maxDaresReachedMessage,
	noMaxDaresMessage,
	notAbleToParticipateMessage,
} from 'root/src/shared/constants/dropEvent'
import { sentryClientObjMessage } from 'root/src/client/util/sentry'
import { useDropEvent } from 'root/src/client/logic/dropEvent/hooks/useDropEvent'
import { useUserCreatorData } from 'root/src/client/logic/auth/hooks/useUser'
import { pick } from 'remeda'
import { getReadablePlatform } from 'root/src/shared/platforms/helpers/getReadablePlatform'
import { getMessageLockedByTier } from 'root/src/client/v2/battlePass/helpers/battlePassMessage.helpers'
import { ProjectStatus } from 'root/src/shared/@types/project'
import { useModal } from 'root/src/client/v2/common/contexts/modal.context'
import { DeliverDareModal } from 'root/src/client/web/list/listItemComponents/dare/DeliverDareModal'
import { useTour } from '@reactour/tour'
import analyticsSendItem from 'root/src/client/logic/analytics/util/analyticsSendItem'
import { DROPS_ANALYTIC_EVENTS } from 'root/src/shared/drops/drops.analytics'
import { useOnboardingQueryParam } from 'root/src/client/v2/onboarding/hooks/useOnboardingQueryParam'
import { trpc } from 'root/src/client/v2/trpc/trpc'
import { CompletionType } from 'root/src/shared/rewards/constants'
import { memo } from 'react'
import { getClassifiedDarePlatform } from 'root/src/shared/drops/helpers/dropDare.helpers'
import { BaseTooltip, BaseTooltipContent } from 'root/src/client/v2/common/components/BaseTooltip/BaseTooltip'
import { routeUrls } from 'root/src/shared/descriptions/routes/routeUrls'

const getButtonText = (projectAssignees, subscribed, comingSoon, canAccessCreateBrandProject, isAccepted, status) => {
	if (isAccepted) {
		if (status === ProjectStatus.DeliveryPending || status === ProjectStatus.DeliveryRejected) {
			return 'RE-DELIVER DARE VIDEO'
		}
		return 'DELIVER DARE VIDEO'
	}
	if (canAccessCreateBrandProject) {
		return 'CREATE DARE'
	}

	if (comingSoon) {
		return subscribed ? 'NOT AVAILABLE NOW' : 'JOIN DROP'
	}
	if (status === ProjectStatus.Rejected) {
		return 'RE-ACCEPT DARE'
	}

	return projectAssignees ? 'PLEDGE' : 'ACCEPT DARE'
}

// TODO we should probably merge display tooltip and tooltip message logic
function DareButtonTooltip({ classes }) {
	const {
		extraData: {
			comingSoon,
			user: {
				userId,
				isAbleToParticipate,
				isDarePlatformAvailable,
				isMaxDaresReached,
				maxDares,
				isLockedByTier,
			},
			isAccepted,
			dareTimeLeft,
			dareCardContext,
		},
		dare: {
			battlePass,
			completion,
			platforms,
		},
	} = useDareCard()
	const drop = useDropEvent()
	const creatorData = useUserCreatorData()

	if (comingSoon) {
		return null
	}

	if (isAccepted && completion.type === CompletionType.GAME_INTEGRATED) {
		return <CompletionTypeTooltip completionType={completion?.type} />
	}

	if (typeof dareTimeLeft === 'number' && dareTimeLeft <= 0) {
		return (
			<p className={classes.buttonTooltip}>
				{dareExpiredMessage}
			</p>
		)
	}

	if (!isAbleToParticipate) {
		sentryClientObjMessage('User not able to participate message', {
			userId,
			dareCardContext: pick(dareCardContext, ['isAbleToParticipate']),
			creatorData,
			drop: pick(drop, ['isAbleToParticipate']),
		})
		return (
			<p className={classes.buttonTooltip}>
				{notAbleToParticipateMessage}
			</p>
		)
	}

	if (!isDarePlatformAvailable) {
		const readablePlatform = getMissingReadablePlatforms(platforms)

		return (
			<p className={classes.buttonTooltip}>
				This Dare is only for {readablePlatform}<br />
				You can connect it <Link href={routeUrls.connectedChannels()}><a className="text-link-purple underline">here</a></Link>
			</p>
		)
	}

	if (isLockedByTier) {
		return (
			<p className={classes.buttonTooltip}>
				{getMessageLockedByTier(battlePass?.tier || 1)}
			</p>
		)
	}

	if (isMaxDaresReached) {
		return (
			<p className={classes.buttonTooltip}>
				{maxDaresReachedMessage}
			</p>
		)
	}

	if (!maxDares) {
		return (
			<p className={classes.buttonTooltip}>
				{noMaxDaresMessage}
			</p>
		)
	}

	return null
}

const getMissingReadablePlatforms = (darePlatforms) => {
	const platforms = new Set(darePlatforms?.map((darePlatform) => {
		const platform = getClassifiedDarePlatform(darePlatform)
		return getReadablePlatform(platform)
	}))
	return [...platforms].join(', ')
}

export const DareCardHeader = withProjectCardStyle(({ classes }) => {
	const {
		isLoading,
		dare: {
			games,
			title,
			creatorDareExpirationDate,
			status,
			projectUrl,
		},
		actions: {
			onAccept,
			onEmailSubscribe,
			refetchDropEvent,
		},
		extraData: {
			labels,
			isAccepted,
			user: {
				isDareButtonEnabled,
				isEligibleToCreateDare,
			},
			dareCardContext: {
				projectDeliveries,
				projectAssignees, scrollPosition,
				emailSubscription, comingSoon,
				canAccessCreateBrandProject, dumb,
			},
			isInteractable,
			creatorDareTimeLeft,
			dareTimeLeft,
		},
	} = useDareCard()

	const { isHovered } = useBlackCardContext()
	const modal = useModal()

	const tour = useTour()
	const [isOnboarding, setIsOnboarding] = useOnboardingQueryParam()

	const queryUtils = trpc.useUtils()

	const handleCreatorOnboarding = async () => {
		const creatorOnboardingState = await queryUtils.creators.getCreatorOnboardingState.fetch()
		if (creatorOnboardingState?.hasSubmittedDare) {
			return
		}
		queryUtils.creators.getCreatorOnboardingState.setData(undefined, () => ({
			...creatorOnboardingState,
			hasSubmittedDare: true,
		}))
	}

	const onClick = async () => {
		if (!isEligibleToCreateDare && comingSoon) {
			await onEmailSubscribe()
			return
		}
		if (status === ProjectStatus.ProjectAccepted || status === ProjectStatus.DeliveryPending) {
			tour.setIsOpen(false)
			modal.show(
				<DeliverDareModal
					projectUrl={projectUrl}
					onSuccess={async () => {
						await Promise.allSettled([
							handleCreatorOnboarding(),
							refetchDropEvent(),
						])
						if (isOnboarding) {
							analyticsSendItem(DROPS_ANALYTIC_EVENTS.DROP_ONBOARDING_COMPLETED)
							setIsOnboarding(undefined)
						}
					}}
				/>,
			)
			return
		}
		await onAccept()
	}

	return (
		<div className={classes.projectCardHeader}>
			{!dumb && isHovered && isInteractable && (
				<div className={classes.ctaButtonOverlay}>
					<BaseTooltip
						tooltip={(
							<BaseTooltipContent className="bg-dark-2 max-w-[300px]">
								<DareButtonTooltip classes={classes} />
							</BaseTooltipContent>
						)}
						hide={isDareButtonEnabled}
					>
						<LoadingButtonNew
							disabled={!isDareButtonEnabled}
							loading={isLoading}
							onClick={onClick}
							type="secondary"
						>
							{getButtonText(
								projectAssignees,
								emailSubscription,
								comingSoon,
								canAccessCreateBrandProject,
								isAccepted,
								status,
							)}
						</LoadingButtonNew>
					</BaseTooltip>
				</div>
			)}
			<LazyLoadImage
				src={addHttpsProtocol(projectDeliveries ? projectDeliveries.thumbnail : games[0].image)}
				alt={title}
				scrollPosition={scrollPosition}
				className={classNames({
					[classes.thumbnail]: true,
					[classes.image]: true,
					[classes.smOpacity]: true,
					[classes.tiktokThumbnail]: projectDeliveries?.platform === 'tiktok',
					[classes.scaledUp]: isHovered,
					[classes.scaledDown]: !isHovered,
				})}
			/>
			<div className={classes.badgesWrapper}>
				<div className={classes.badgesContainer}>
					<div className={classes.marksContainer}>
						{labels.map(label => (
							<ProjectCardLabel
								key={label}
								status={statusLabels[label]}
								color={statusColorsMap[label]}
							/>
						))}
					</div>
					{
						(typeof dareTimeLeft === 'number' || typeof creatorDareTimeLeft === 'number')
						&& (
							<ProjectTimeLeft
								timeLeft={creatorDareTimeLeft || dareTimeLeft}
								expirationDate={creatorDareExpirationDate}
							/>
						)
					}
				</div>
			</div>
		</div>
	)
})

const CompletionTypeTooltip = memo(({ completionType }) => {
	if (completionType === CompletionType.GAME_INTEGRATED) {
		return (
			<p className="z-[1000]">
				This Dare is integrated with the game<br /> you can complete it by playing the game<br /> and receive the reward inside the game.
			</p>
		)
	}

	return null
})
